import { del, get, update } from "idb-keyval";
import { z } from "zod";
import { MV_ACTIVITY_TYPES } from "@/db/schema/movement";
import type { ActiveActivity } from "@/hooks/use-movement";
import { createRandomId } from "@/lib/random-id";
import type { GPSPoint } from "./gps-utils";
import { gpsSampleSchema, mergeGPSStreams } from "./sample-protocol";

type Storage = Pick<globalThis.Storage, "getItem" | "setItem" | "removeItem">;
const sessionKey = (accountId: string) =>
  `hiive:movement:session:v2:${accountId}`;
const pointsKey = (accountId: string, clientId: string) =>
  `hiive:movement:samples:v2:${accountId}:${clientId}`;
const activitySchema = z.object({
  id: z.string().min(1),
  clientId: z.string().min(1),
  accountId: z.string().min(1),
  activityType: z.enum(MV_ACTIVITY_TYPES),
  autoType: z.boolean().optional(),
  savedActivityType: z.enum(MV_ACTIVITY_TYPES).optional(),
  startedAt: z.iso.datetime({ offset: true }),
  isPaused: z.boolean(),
  pauseReason: z.enum(["manual", "auto"]).nullish(),
  pausedAt: z.iso.datetime({ offset: true }).nullable(),
  totalPausedSeconds: z.number().min(0),
  lastControlAtMs: z.number().finite().nonnegative().optional(),
  segmentId: z.number().int().nonnegative().optional(),
  endedAt: z.iso.datetime({ offset: true }).optional(),
  disposition: z.enum(["completed", "discarded"]).optional(),
  name: z.string().max(200).optional(),
  notes: z.string().max(10000).optional(),
  isPublic: z.boolean().optional(),
  moduleActivationId: z.number().int().positive().nullish(),
  contextTitle: z.string().nullish(),
  routeId: z.string().nullish(),
  liveSessionId: z.number().nullish(),
});

export function readMovementJournal(
  storage: Storage,
  accountId: string,
): ActiveActivity | null {
  const raw = storage.getItem(sessionKey(accountId));
  if (!raw) return null;
  try {
    const record = JSON.parse(raw);
    const parsed = activitySchema.safeParse(record.activity);
    if (
      record.schemaVersion !== 2 ||
      !parsed.success ||
      parsed.data.accountId !== accountId
    )
      throw new Error("invalid journal");
    return parsed.data;
  } catch {
    throw new Error(
      "The saved movement recording needs recovery. Its data has been retained.",
    );
  }
}

export function writeMovementJournal(
  storage: Storage,
  accountId: string,
  activity: ActiveActivity | null,
) {
  if (!accountId) throw new Error("Sign in before recording movement");
  if (!activity) {
    storage.removeItem(sessionKey(accountId));
    return;
  }
  if (activity.accountId !== accountId)
    throw new Error("The recording belongs to a different account");
  activitySchema.parse(activity);
  const current = readMovementJournal(storage, accountId);
  if (current) {
    if (
      current.clientId !== activity.clientId ||
      current.startedAt !== activity.startedAt ||
      current.activityType !== activity.activityType
    )
      throw new Error(
        "Another recording is still saved on this device. Finish recovering it first.",
      );
    if (
      current.endedAt &&
      (current.endedAt !== activity.endedAt ||
        current.disposition !== activity.disposition ||
        current.totalPausedSeconds !== activity.totalPausedSeconds ||
        current.name !== activity.name ||
        current.notes !== activity.notes ||
        current.isPublic !== activity.isPublic)
    )
      throw new Error(
        "This recording already has a saved finish or discard request. Retry that request first.",
      );
  }
  storage.setItem(
    sessionKey(accountId),
    JSON.stringify({ schemaVersion: 2, activity }),
  );
}

export function identifyGPSSamples(points: GPSPoint[]): GPSPoint[] {
  return points.map((point) =>
    point.sampleId
      ? point
      : {
          ...point,
          sampleId: createRandomId(),
          segmentId: point.segmentId ?? 0,
        },
  );
}

export async function readMovementSamples(
  accountId: string,
  clientId: string,
): Promise<GPSPoint[]> {
  const raw = await get(pointsKey(accountId, clientId));
  if (raw === undefined) return [];
  const parsed = z
    .array(gpsSampleSchema.extend({ sampleId: z.string().min(1) }))
    .safeParse(raw);
  if (!parsed.success)
    throw new Error(
      "Saved GPS samples need recovery. Their original data has been retained.",
    );
  return parsed.data;
}

const writes = new Map<string, Promise<void>>();
/** Serialize writes by owner/session. A stale callback cannot overwrite another run. */
export function writeMovementSamples(
  accountId: string,
  clientId: string,
  points: GPSPoint[],
): Promise<void> {
  if (!accountId || !clientId)
    return Promise.reject(new Error("Missing recording identity"));
  const key = pointsKey(accountId, clientId);
  const previous = writes.get(key);
  const next = (previous ? previous.catch(() => {}) : Promise.resolve()).then(
    // IndexedDB serializes this read/merge/write across tabs too. Replacing the
    // array lets a stale tab erase samples collected by the current recorder.
    () =>
      update<GPSPoint[]>(key, (stored) =>
        mergeGPSStreams(stored ?? [], points),
      ),
  );
  writes.set(key, next);
  // The caller observes failures. Keep the promise for flush even after settlement.
  void next.catch(() => {});
  return next;
}

export async function flushMovementSamples(
  accountId: string,
  clientId: string,
) {
  await writes.get(pointsKey(accountId, clientId));
}

/** Call only after the server has acknowledged this exact recording's terminal intent. */
export async function removeMovementSamples(
  accountId: string,
  clientId: string,
) {
  const key = pointsKey(accountId, clientId);
  await flushMovementSamples(accountId, clientId);
  await del(key);
  writes.delete(key);
}

export function freezeMovementActivity(
  activity: ActiveActivity,
  now: number,
  changes: Pick<
    ActiveActivity,
    "name" | "notes" | "isPublic" | "disposition" | "savedActivityType"
  > = {},
): ActiveActivity {
  if (activity.endedAt) {
    if (changes.disposition && changes.disposition !== activity.disposition)
      throw new Error(
        "This recording already has a saved finish or discard request. Retry that request first.",
      );
    // The saved type is not identity: a save that failed because the route
    // didn't fit the chosen type ("walk" dropping a run's points) must be
    // retryable as another type, not stuck repeating the same failure.
    if (
      "savedActivityType" in changes &&
      changes.savedActivityType !== activity.savedActivityType
    ) {
      return { ...activity, savedActivityType: changes.savedActivityType };
    }
    return activity;
  }
  const endedAt = new Date(
    Math.max(Date.parse(activity.startedAt), now),
  ).toISOString();
  const pause = activity.pausedAt
    ? Math.max(0, (Date.parse(endedAt) - Date.parse(activity.pausedAt)) / 1000)
    : 0;
  return {
    ...activity,
    ...changes,
    endedAt,
    disposition: changes.disposition ?? "completed",
    isPaused: true,
    pauseReason: "manual",
    pausedAt: endedAt,
    totalPausedSeconds: activity.totalPausedSeconds + pause,
  };
}

/** Control timestamps, not delivery time, determine paused duration. */
export function applyMovementPause(
  activity: ActiveActivity,
  paused: boolean,
  at: number,
  reason: "manual" | "auto" = "manual",
): ActiveActivity {
  if (
    activity.endedAt ||
    !Number.isFinite(at) ||
    at < Date.parse(activity.startedAt) ||
    at <= (activity.lastControlAtMs ?? 0)
  )
    return activity;
  const next = { ...activity, lastControlAtMs: at };
  if (activity.isPaused === paused) return next;
  return {
    ...next,
    isPaused: paused,
    pausedAt: paused ? new Date(at).toISOString() : null,
    pauseReason: paused ? reason : null,
    totalPausedSeconds:
      activity.totalPausedSeconds +
      (!paused && activity.pausedAt
        ? Math.max(0, (at - Date.parse(activity.pausedAt)) / 1000)
        : 0),
    segmentId: (activity.segmentId ?? 0) + (paused ? 0 : 1),
  };
}
